import * as Dialog from "@radix-ui/react-dialog";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import Button from "./button";
import Input from "./input";
import { useForms } from "@/modules/form/hooks/use-forms";
import type { FormPreset } from "@/modules/form/types";

interface CreateFormModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: (formId: string) => void;
	onSubmit: (data: FormFormValues) => Promise<{ success: boolean; data?: { id: string } }>;
}

export type FormFormValues = z.infer<typeof formFormSchema>;

const formFormSchema = z.object({
	title: z.string().min(1, "Form title is required").max(200, "Max 200 characters"),
	preset_id: z.string().nullable().optional(),
});

export function CreateFormModal({ open, onOpenChange, onSuccess, onSubmit }: CreateFormModalProps) {
	const [error, setError] = useState<string | null>(null);
	const { presets, listPresets } = useForms();
	const [isLoadingPresets, setIsLoadingPresets] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors, isSubmitting },
	} = useForm<FormFormValues>({
		resolver: zodResolver(formFormSchema),
		defaultValues: {
			title: "",
			preset_id: null,
		},
	});

	const selectedPresetId = watch("preset_id");

	useEffect(() => {
		if (open && !presets) {
			setIsLoadingPresets(true);
			listPresets().finally(() => setIsLoadingPresets(false));
		}
	}, [open, presets, listPresets]);

	const handlePresetSelect = (preset: FormPreset | null) => {
		setValue("preset_id", preset?.id ?? null, { shouldValidate: true });
	};

	const handleFormSubmit = async (data: FormFormValues) => {
		setError(null);
		try {
			const result = await onSubmit(data);
			if (result.success && result.data) {
				onSuccess(result.data.id);
				handleReset();
				onOpenChange(false);
			} else {
				setError("Failed to create form. Please try again.");
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred while creating the form.");
		}
	};

	const handleReset = () => {
		reset();
		setError(null);
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			handleReset();
		}
		onOpenChange(newOpen);
	};

	return (
		<Dialog.Root open={open} onOpenChange={handleOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-4xl modal-content overflow-hidden">
					<form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col max-h-[80vh]">
						{/* Header */}
						<div className="px-6 pt-6 pb-4 border-b border-gray-200">
							<Dialog.Title className="text-base font-semibold text-gray-900 leading-base">Create Form</Dialog.Title>
							<Dialog.Description className="text-sm text-gray-500 leading-sm mt-1">Choose a preset and enter a title. You&apos;ll be taken to the builder to customize it.</Dialog.Description>
						</div>

						{/* Content */}
						<div className="overflow-y-auto px-6 py-4">
							<div className="space-y-5">
								<div className="max-w-sm">
									<Input
										id="form-title"
										type="text"
										label="Form Title"
										placeholder="Enter form title"
										required
										disabled={isSubmitting}
										error={errors.title?.message}
										{...register("title")}
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700">Preset</label>
									<p className="text-xs text-gray-500 mt-0.5 mb-2">Pick the one that fits best. You can customize all the content later.</p>
									{isLoadingPresets ? (
										<div className="grid grid-cols-3 gap-3">
											{Array.from({ length: 3 }).map((_, i) => (
												<div key={i} className="rounded-xl border-2 border-gray-200 p-3 animate-pulse">
													<div className="h-20 w-full rounded-lg bg-gray-100 mb-2" />
													<div className="h-3 w-16 bg-gray-100 rounded mb-1" />
													<div className="h-2.5 w-24 bg-gray-100 rounded" />
												</div>
											))}
										</div>
									) : (
										<div className="grid grid-cols-3 gap-3">
											<button
												type="button"
												onClick={() => handlePresetSelect(null)}
												disabled={isSubmitting}
												className={`group flex flex-col h-44 text-left rounded-xl border-2 overflow-hidden transition-all ${!selectedPresetId ? "border-brand ring-2 ring-brand/20" : "border-gray-200 hover:border-gray-300"}`}
											>
												<div className="flex-1 min-h-0 bg-gray-50 flex items-center justify-center">
													<svg className="h-8 w-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
													</svg>
												</div>
												<div className="px-4 py-3 shrink-0">
													<h4 className="text-sm font-semibold text-gray-900">Start from scratch</h4>
													<p className="text-xs text-tertiary line-clamp-1">Use system defaults</p>
												</div>
											</button>
											{(presets || []).map((preset) => {
												const firstQuestion = preset.questions?.find((q) => q.is_primary === 1) ?? preset.questions?.[0];
												return (
													<button
														key={preset.id}
														type="button"
														onClick={() => handlePresetSelect(preset)}
														disabled={isSubmitting}
														className={`group flex flex-col h-44 text-left rounded-xl border-2 overflow-hidden transition-all ${selectedPresetId === preset.id ? "border-brand ring-2 ring-brand/20" : "border-gray-200 hover:border-gray-300"}`}
													>
														<div className="flex-1 min-h-0 bg-gray-50 flex items-center justify-center px-4">
															{firstQuestion ? (
																<p className="text-xs text-gray-500 text-center line-clamp-3 italic">&ldquo;{firstQuestion.title}&rdquo;</p>
															) : (
																<span className="text-xs text-gray-400">Preview</span>
															)}
														</div>
														<div className="px-4 py-3 shrink-0">
															<h4 className="text-sm font-semibold text-gray-900">{preset.name}</h4>
															<p className="text-xs text-tertiary line-clamp-1">{preset.description}</p>
														</div>
													</button>
												);
											})}
										</div>
									)}
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
							<Dialog.Close asChild>
								<Button type="button" variant="soft">
									Cancel
								</Button>
							</Dialog.Close>
							<div className="flex items-center gap-3">
								{error && <p className="text-sm text-red-600 leading-sm">{error}</p>}
								<Button type="submit" variant="brand" disabled={isSubmitting}>
									{isSubmitting ? "Creating..." : "Create & Go to Builder"}
								</Button>
							</div>
						</div>
					</form>

					<Dialog.Close asChild>
						<button className="absolute top-7 right-6 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
							<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</Dialog.Close>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
